import { Grid } from "@minervaproject/fds/es/components/grid";
import { useDrop } from "react-dnd";

import { BREAKOUT_ROOM_USER_DRAG_TYPE, BreakoutRoomUsers } from "./breakout_room_users";
import { ListenButton } from "./listen_button";
import { PinButton } from "./pin_button";
import { VisitButton } from "./visit_button";

import { CenteredErrorBoundary } from "components/error_boundary";
import { Stage } from "components/stage";
import { useCapabilities, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { moveBreakoutRoomUser } from "shared/reducers/classroom";
import { BreakoutRoomType, CapabilityType } from "shared/types";
import { UserType } from "shared/types/user";
import { classroomSelectors } from "store";

import "./breakout_rooms_preview.scss";

const getColumns = (n: number) => {
  switch (n) {
    case 1:
      return "1fr";
    case 2:
    case 3:
    case 4:
      return "1fr 1fr";
    case 5:
    case 6:
      return "1fr 1fr 1fr";
    case 7:
    case 8:
      return "1fr 1fr 1fr 1fr";
    default:
      return "1fr 1fr 1fr 1fr 1fr";
  }
};

function BreakoutRoomPreviewItem({
  breakoutRoom,
  breakoutRoomUsers,
  totalNumberOfBreakoutRooms,
  hasAv,
}: {
  breakoutRoom: BreakoutRoomType;
  breakoutRoomUsers: UserType[];
  totalNumberOfBreakoutRooms: number;
  hasAv?: boolean;
}) {
  const dispatch = useAppDispatch();
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const { canControlClassroom, canVisitBreakouts } = useCapabilities();
  const [, drop] = useDrop({
    accept: [BREAKOUT_ROOM_USER_DRAG_TYPE],
    drop: (item: { userId: string; oldBreakoutRoomId: string }) => {
      if (item.oldBreakoutRoomId !== breakoutRoom.id) {
        dispatch(
          moveBreakoutRoomUser({
            userId: item.userId,
            oldBreakoutRoomId: item.oldBreakoutRoomId,
            newBreakoutRoomId: breakoutRoom.id,
          }),
        );
      }
    },
  });

  if (isPresentationView && breakoutRoomUsers.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-column"
      data-testid="group-preview-item"
      style={{ minHeight: 0, minWidth: 0 }} // prevents item from stretching to fit content
      ref={drop}
    >
      <div className="flex justify-between items-center p2 bg-black-tint-20">
        <div className="bold text-black-tint-70">Breakout Group {breakoutRoom.title}</div>
        <div className="flex place-items-end">
          {canVisitBreakouts && <VisitButton breakoutRoom={breakoutRoom} />}
          {hasAv && <ListenButton breakoutRoom={breakoutRoom} />}
          {canControlClassroom && <PinButton breakoutRoom={breakoutRoom} />}
        </div>
      </div>
      <BreakoutRoomUsers
        breakoutRoom={breakoutRoom}
        users={breakoutRoomUsers}
        totalNumberOfBreakoutRooms={totalNumberOfBreakoutRooms}
        hasAv={hasAv}
      />
      <div className="flex flex-auto" data-testid={`group-preview-stage-${breakoutRoom.title}`}>
        <CenteredErrorBoundary text="The stage cannot be displayed">
          <Stage panes={breakoutRoom.panes} />
        </CenteredErrorBoundary>
      </div>
    </div>
  );
}

function BreakoutRoomsPreview({ breakoutRooms }: { breakoutRooms: BreakoutRoomType[] }) {
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const allClassUsers = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat("is_present")),
  ) as UserType[];
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const columns = getColumns(breakoutRooms.length);

  return (
    <Grid
      className="px4 h-full breakout-rooms-preview"
      columnGap={4}
      rowGap={4}
      columns={columns}
      autoRows="1fr"
    >
      {breakoutRooms.map((breakoutRoom) => (
        <CenteredErrorBoundary
          text="This breakout group preview cannot be displayed"
          key={breakoutRoom.id}
        >
          <BreakoutRoomPreviewItem
            breakoutRoom={breakoutRoom}
            breakoutRoomUsers={allClassUsers.filter(
              ({ id }) =>
                isVisibleInClassUserIds.includes(id) &&
                (breakoutRoom.assigned_user_ids.includes(id) ||
                  breakoutRoom.visiting_user_ids.includes(id)),
            )}
            totalNumberOfBreakoutRooms={breakoutRooms.length}
            hasAv={!isInPerson}
          />
        </CenteredErrorBoundary>
      ))}
    </Grid>
  );
}

export { BreakoutRoomPreviewItem, BreakoutRoomsPreview, getColumns };
